

import React, { useState, useEffect, useRef } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify, FiSearch, FiX } from "react-icons/fi";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { BiLoader } from "react-icons/bi";
import avatarFallback from "assets/img/avatars/avatar4.png";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config";

const Navbar = (props) => {
  const { onOpenSidenav, brandText } = props;
  const [darkmode, setDarkmode] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [admin, setAdmin] = useState(null);
  

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);


  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/admin/get-admin`,
          {
            headers: { token },
          }
        );
        const adminData = response.data?.result?.admin;
        if (adminData) {
          setAdmin(adminData);
        } else {
          toast.error("Admin not found");
        }
      } catch (error) {
      
        toast.error("Failed to load admin details");
      }
    };

    if (token) fetchAdmin();
  }, [token]);


  const performSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/global-search?q=${encodeURIComponent(query)}`,
        {
          headers: { token },
        }
      );
   
      if (response.data.success) {
        setSearchResults(response.data.result);
        setShowSearchResults(true);
      } else {
        toast.error(response.data.message || "Search failed");
        setSearchResults(null);
        setShowSearchResults(false);
      }
    } catch (error) {
    
      toast.error("Search failed. Please try again.");
      setSearchResults(null);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

 
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

  
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }


    searchTimeout.current = setTimeout(() => {
      performSearch(query);
    }, 500);
  };


  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
    setShowSearchResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);


  const handleLogout = async () => {
    const loadingToast = toast.loading("Please wait...");
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/admin-logout`,
        {},
        { headers: { token } }
      );
      localStorage.removeItem("token");
      localStorage.removeItem("AdminId");
      toast.success("Logout Successfully");
      navigate("/auth/");
    } catch (err) {
      toast.error("Logout failed");
    } finally {
      toast.dismiss(loadingToast);
    }
  };


  const avatarUrl = admin?.adminName
    ? `https://ui-avatars.com/api/?name=${admin.adminName}`
    : avatarFallback;

 
  const renderSearchResults = () => {
    if (!searchResults) return null;

    const { customers, projects, visits, bills, finalBills } = searchResults;
    const hasResults = customers?.length || projects?.length || visits?.length || bills?.length || finalBills?.length;

    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-navy-800 rounded-xl shadow-xl border border-gray-200 dark:border-navy-600 max-h-96 overflow-y-auto z-50">
        {!hasResults ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            No results found for "{searchQuery}"
          </div>
        ) : (
          <div className="p-2">
        
            {customers?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 px-2">
                  Customers ({customers.length})
                </h3>
                {customers.slice(0, 3).map((customer) => (
                  <div
                    key={customer._id}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/customer-management`);
                      setShowSearchResults(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-navy-700 dark:text-white">
                        {customer.customerName}
                      </span>
                      <span className="text-sm text-gray-500">
                        {customer.customerNumber}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.requiredFlatType} in {customer.locationRequired}
                    </div>
                  </div>
                ))}
              </div>
            )}

           
            {projects?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 px-2">
                  Projects ({projects.length})
                </h3>
                {projects.slice(0, 3).map((project) => (
                  <div
                    key={project._id}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/project-management`);
                      setShowSearchResults(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-navy-700 dark:text-white">
                        {project.projectName}
                      </span>
                      <span className="text-sm text-gray-500">
                        {project.propertyType}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {project.area} - {project.flatType}
                    </div>
                  </div>
                ))}
              </div>
            )}

       
            {bills?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 px-2">
                  Bills ({bills.length})
                </h3>
                {bills.slice(0, 2).map((bill) => (
                  <div
                    key={bill._id}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/billing-management`);
                      setShowSearchResults(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-navy-700 dark:text-white">
                        {bill.customerId?.customerName}
                      </span>
                      <span className="text-sm text-green-600 font-semibold">
                        ₹{bill.totalAmount?.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {bill.projectId?.projectName}
                    </div>
                  </div>
                ))}
              </div>
            )}

          
            {finalBills?.length > 0 && (
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 px-2">
                  Final Bills ({finalBills.length})
                </h3>
                {finalBills.slice(0, 2).map((bill) => (
                  <div
                    key={bill._id}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/billing-management`);
                      setShowSearchResults(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-navy-700 dark:text-white">
                        {bill.customerId?.customerName}
                      </span>
                      <span className="text-sm text-blue-600 font-semibold">
                        ₹{bill.finalAgreedPrice?.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {bill.projectId?.projectName}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
        <div className="ml-[6px]">
          <p className="shrink text-[33px] capitalize text-navy-700 dark:text-white">
            <Link
              to="#"
              className="font-bold capitalize hover:text-navy-700 dark:hover:text-white"
            >
              {brandText}
            </Link>
          </p>
        </div>

        <div className="relative mt-[3px] flex h-[61px] w-[355px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:w-[365px] md:flex-grow-0 md:gap-1 xl:w-[365px] xl:gap-2">
          
          <div ref={searchRef} className="relative flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[225px]">
            <p className="pl-3 pr-2 text-xl">
              {isSearching ? (
                <BiLoader className="h-4 w-4 animate-spin text-gray-400 dark:text-white" />
              ) : (
                <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
              )}
            </p>
            <input
              type="text"
              placeholder="Search customers, projects..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
              onFocus={() => {
                if (searchResults) setShowSearchResults(true);
              }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}

            
            {showSearchResults && renderSearchResults()}
          </div>

          <span
            className="flex cursor-pointer text-xl text-gray-600 dark:text-white xl:hidden"
            onClick={onOpenSidenav}
          >
            <FiAlignJustify className="h-5 w-5" />
          </span>

          <div
            className="cursor-pointer text-gray-600"
            onClick={() => {
              if (darkmode) {
                document.body.classList.remove("dark");
                setDarkmode(false);
              } else {
                document.body.classList.add("dark");
                setDarkmode(true);
              }
            }}
          >
            {darkmode ? (
              <RiSunFill className="h-4 w-4 text-gray-600 dark:text-white" />
            ) : (
              <RiMoonFill className="h-4 w-4 text-gray-600 dark:text-white" />
            )}
          </div>

        
          <Dropdown
            button={
              <img
                className="h-10 w-10 rounded-full"
                src={avatarUrl}
                alt={admin?.adminName || "Admin"}
              />
            }
            children={
              <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      👋 Hey, {admin?.adminName || "Admin"}
                    </p>
                  </div>
                </div>
                <div className="h-px w-full bg-gray-200 dark:bg-white/20" />
                <div className="flex flex-col p-4">
                  <Link
                    to="/admin/profile"
                    className="text-sm text-gray-800 dark:text-white hover:dark:text-white"
                  >
                    Profile Settings
                  </Link>
                  <button
                    onClick={() => setShowLogoutPopup(true)}
                    className="mt-3 text-left text-sm font-medium text-red-500 transition duration-150 ease-out hover:text-red-500 hover:ease-in"
                  >
                    Log Out
                  </button>
                </div>
              </div>
            }
            classNames={"py-2 top-8 -left-[180px] w-max"}
          />
        </div>
      </nav>


      {showLogoutPopup && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="w-[300px] rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
              Are you sure you want to logout?
            </h2>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutPopup(false)}
                className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="rounded-md bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-500"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;

